import React, { useState, useEffect } from 'react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import { fetchLitigations } from '../api';
import { useTranslation } from 'react-i18next';
const IMAGE_BASE_URL = process.env.REACT_APP_IMAGE_BASE_URL!;

interface Litigation {
  _id: string;
  description: { en: string; ar: string };
  priority: number;
  isActive: boolean;
  image: string;
}

interface CarouselItem {
  src: string;
  description: string;
}

const StrategicLitigation: React.FC = () => {
  const [litigations, setLitigations] = useState<CarouselItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<CarouselItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  // Fetch litigations on component mount
  useEffect(() => {
    const loadLitigations = async () => {
      try {
        const data = await fetchLitigations();
        // Filter active litigations and sort by priority
        const activeLitigations = data.litigations
          .filter((l: Litigation) => l.isActive)
          .sort((a: Litigation, b: Litigation) => a.priority - b.priority)
          .map((l: Litigation) => ({
            src: `${IMAGE_BASE_URL}/${l.image}`,
            description:
              i18n.language === 'ar' ? l.description.ar : l.description.en,
          }));
        setLitigations(activeLitigations);
        // Set initial selected item if available
        if (activeLitigations.length > 0) {
          setSelectedItem(activeLitigations[0]);
        }
        setLoading(false);
      } catch (err) {
        setError(t('errors.fetchLitigations'));
        setLoading(false);
      }
    };
    loadLitigations();
  }, [t, i18n.language]);

  const responsive = {
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 2,
      slidesToSlide: 1,
    },
    tablet: { breakpoint: { max: 1024, min: 464 }, items: 2, slidesToSlide: 1 },
    mobile: { breakpoint: { max: 464, min: 0 }, items: 1, slidesToSlide: 1 },
  };

  const handleImageClick = (item: CarouselItem) => {
    setSelectedItem(item);
  };

  if (loading) {
    return <div>{t('loading')}</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  if (litigations.length === 0 || !selectedItem) {
    return <div>{t('noLitigations')}</div>;
  }

  return (
    <section className="Strategic_Litigation">
      <div className="container">
        <div className="row">
          <div className="col-md-12 text-center">
            <div className="content_wrapper">
              <h2>{t('strategic_litigation_experts')}</h2>
            </div>
          </div>
        </div>
        <div className="main_wrapes">
          <div className="supper_calls">
            <div
              className="left_cols"
              style={{ direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}
            >
              <img
                src={selectedItem.src}
                className="img-fluid"
                alt={t('strategicLitigationPreview')}
              />
              <p
                style={{
                  textAlign: i18n.language === 'ar' ? 'right' : 'justify',
                }}
                className="text-justify"
              >
                {selectedItem.description}
              </p>
            </div>
            <div className="right_cols">
              <Carousel
                responsive={responsive}
                infinite={true}
                autoPlay={true}
                autoPlaySpeed={3000}
                showDots={true}
                arrows={false}
                className="owl-carousel owl-theme"
                itemClass="px-3 pb-5"
              >
                {litigations.map((item, index) => (
                  <div
                    className="item"
                    key={index}
                    onClick={() => handleImageClick(item)}
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="image_wrraper">
                      <img
                        src={item.src}
                        alt={`${t('strategicItem')} ${index + 1}`}
                      />
                    </div>
                  </div>
                ))}
              </Carousel>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        .supper_calls p {
          direction: ${i18n.language === 'ar' ? 'rtl' : 'ltr'};
          text-align: ${i18n.language === 'ar' ? 'right' : 'left'};
        }
      `}</style>
    </section>
  );
};

export default StrategicLitigation;
